import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCcw, ExternalLink, Loader2, AlertTriangle,
  Plus, Trash2, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categories we render as gauges on each row. Order matches WhyIQ's own.
const SCORE_FIELDS = [
  { key: 'whyiq_score',    label: 'WhyIQ' },
  { key: 'ai_citability',  label: 'AI Citability' },
  { key: 'search_ranking', label: 'Search' },
  { key: 'accessibility',  label: 'A11y' },
  { key: 'cta_pull',       label: 'CTA Pull' },
];

function scoreColor(score) {
  if (score == null) return 'text-neutral-500';
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-[#00F0FF]';
  if (score >= 40) return 'text-yellow-400';
  return 'text-red-400';
}

function deltaIcon(curr, prev) {
  if (curr == null || prev == null) return null;
  const d = curr - prev;
  if (d > 0) return <TrendingUp className="w-3 h-3 text-green-400" />;
  if (d < 0) return <TrendingDown className="w-3 h-3 text-red-400" />;
  return <Minus className="w-3 h-3 text-neutral-500" />;
}

function relativeDate(iso) {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return dt.toLocaleDateString();
  } catch { return '—'; }
}

// Lightweight SVG sparkline — keeps the bundle small (no charting lib).
function ScoreSparkline({ points, color = '#00F0FF', width = 360, height = 80 }) {
  if (!points || points.length < 2) {
    return (
      <div className="flex items-center justify-center text-xs text-neutral-500 italic" style={{ height }}>
        Import 2+ scans to see a trend line.
      </div>
    );
  }
  const minY = 0;
  const maxY = 100;
  const stepX = width / Math.max(1, points.length - 1);
  const yFor = (v) => height - ((v - minY) / (maxY - minY)) * height;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX},${yFor(p)}`).join(' ');
  const last = points[points.length - 1];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="w-full">
      {/* axis lines at 25 / 50 / 75 */}
      {[25, 50, 75].map((y) => (
        <line key={y} x1={0} y1={yFor(y)} x2={width} y2={yFor(y)}
              stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
      ))}
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {points.map((p, i) => (
        <circle key={i} cx={i * stepX} cy={yFor(p)} r="3" fill={color} />
      ))}
      <text x={width - 6} y={yFor(last) - 6} fill={color} fontSize="11" fontWeight="bold" textAnchor="end">
        {last}
      </text>
    </svg>
  );
}

export default function AdminSEOAudits() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [scanUrl, setScanUrl] = useState('');
  const [pageLabel, setPageLabel] = useState('Homepage');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/seo-audits`, {
        params: { limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAudits(res.data?.audits || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (e) => {
    e.preventDefault();
    if (!scanUrl.trim()) {
      toast.error('Paste a WhyIQ scan URL first');
      return;
    }
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/seo-audits/import`, {
        scan_url: scanUrl.trim(),
        page_label: pageLabel.trim() || 'Homepage',
        note: note.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Audit imported');
      setScanUrl('');
      setNote('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed — check the URL and try again');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (auditId) => {
    if (!window.confirm('Delete this audit row? Trend line will recompute.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/seo-audits/${auditId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Audit deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Group audits by page_label, then sort each group by imported_at ASC
  // so the sparkline reads left→right oldest→newest.
  const groupedByPage = useMemo(() => {
    const groups = {};
    for (const a of audits) {
      const key = a.page_label || 'Homepage';
      (groups[key] = groups[key] || []).push(a);
    }
    for (const k of Object.keys(groups)) {
      groups[k].sort((a, b) => new Date(a.imported_at) - new Date(b.imported_at));
    }
    return groups;
  }, [audits]);

  return (
    <AdminLayout currentPath="/admin/seo-audits" title="SEO Audit Trends">
      <div className="space-y-8">
        {/* Page header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="w-6 h-6 text-[#00F0FF]" />
              SEO Audit Trends
            </h1>
            <p className="text-sm text-neutral-400 mt-1">
              Track WhyIQ scores over time. Run a scan at{' '}
              <a href="https://www.whyiq.ai" target="_blank" rel="noopener noreferrer"
                 className="text-[#00F0FF] hover:underline inline-flex items-center gap-1">
                whyiq.ai <ExternalLink className="w-3 h-3" />
              </a>{' '}
              and paste the results URL below.
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="seo-audits-refresh">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Import form */}
        <div className="bg-neutral-900/60 border border-white/10 rounded-xl p-5" data-testid="seo-audits-import-form">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF] mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Import a scan
          </h2>
          <form onSubmit={handleImport} className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <Input
              type="url"
              placeholder="https://www.whyiq.ai/results/scn_xxxxx"
              value={scanUrl}
              onChange={(e) => setScanUrl(e.target.value)}
              className="md:col-span-6 bg-black/40 border-white/10"
              data-testid="seo-audits-scan-url"
              required
            />
            <Input
              type="text"
              placeholder="Page label (e.g. Homepage, /partners/platforms)"
              value={pageLabel}
              onChange={(e) => setPageLabel(e.target.value)}
              className="md:col-span-3 bg-black/40 border-white/10"
              data-testid="seo-audits-page-label"
            />
            <Input
              type="text"
              placeholder="Note (optional, e.g. 'after Phase 1 redeploy')"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="md:col-span-3 bg-black/40 border-white/10"
              data-testid="seo-audits-note"
            />
            <Button
              type="submit"
              disabled={importing}
              className="md:col-span-12 bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold"
              data-testid="seo-audits-import-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Import scan
            </Button>
          </form>
          <p className="text-xs text-neutral-500 mt-3">
            Importing the same URL twice is safe — we upsert on URL so the trend line stays clean.
          </p>
        </div>

        {/* Trend charts — one sparkline per page_label per metric */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" />
          </div>
        ) : Object.keys(groupedByPage).length === 0 ? (
          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-10 text-center">
            <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
            <p className="text-neutral-400">No audits yet. Run your first scan on{' '}
              <a href="https://www.whyiq.ai" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] underline">whyiq.ai</a>{' '}
              and paste the results URL above.
            </p>
          </div>
        ) : (
          Object.entries(groupedByPage).map(([pageLabel, items]) => (
            <div key={pageLabel} className="space-y-4" data-testid={`seo-audits-page-${pageLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              <h2 className="text-lg font-bold flex items-baseline gap-2">
                <span className="text-[#00F0FF]">{pageLabel}</span>
                <span className="text-xs text-neutral-500 font-normal">· {items.length} {items.length === 1 ? 'scan' : 'scans'}</span>
              </h2>

              {/* Sparkline grid — one per top-line score */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {SCORE_FIELDS.map((f) => {
                  const points = items.map((a) => a[f.key]).filter((v) => v != null);
                  const last = points[points.length - 1];
                  const prev = points[points.length - 2];
                  return (
                    <div key={f.key} className="bg-neutral-900/40 border border-white/10 rounded-xl p-4">
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{f.label}</span>
                        <div className="flex items-center gap-1.5">
                          {deltaIcon(last, prev)}
                          <span className={`text-2xl font-black tabular-nums ${scoreColor(last)}`}>
                            {last == null ? '—' : last}
                          </span>
                        </div>
                      </div>
                      <ScoreSparkline points={points} color="#00F0FF" width={300} height={48} />
                    </div>
                  );
                })}
              </div>

              {/* Recent scans table */}
              <div className="bg-neutral-900/40 border border-white/10 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-neutral-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 px-4">Imported</th>
                      <th className="text-right py-2 px-2">WhyIQ</th>
                      <th className="text-right py-2 px-2">AI Cit.</th>
                      <th className="text-right py-2 px-2">Search</th>
                      <th className="text-right py-2 px-2">A11y</th>
                      <th className="text-right py-2 px-2">CTA</th>
                      <th className="text-left py-3 px-4">Note</th>
                      <th className="text-right py-3 px-4">Source</th>
                      <th className="py-3 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...items].reverse().map((a) => (
                      <tr key={a.audit_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-2 px-4 text-neutral-300 whitespace-nowrap">{relativeDate(a.imported_at)}</td>
                        {SCORE_FIELDS.map((f) => (
                          <td key={f.key} className={`text-right py-2 px-2 font-mono tabular-nums ${scoreColor(a[f.key])}`}>
                            {a[f.key] ?? '—'}
                          </td>
                        ))}
                        <td className="py-2 px-4 text-neutral-400 max-w-xs truncate" title={a.note || ''}>{a.note || '—'}</td>
                        <td className="py-2 px-4 text-right">
                          <a href={a.scan_url} target="_blank" rel="noopener noreferrer"
                             className="text-[#00F0FF] hover:underline inline-flex items-center gap-1 text-xs">
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <button
                            onClick={() => handleDelete(a.audit_id)}
                            className="text-neutral-600 hover:text-red-400 transition-colors p-1"
                            title="Delete audit"
                            data-testid={`seo-audit-delete-${a.audit_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
